import { ORG_SELECTED_KEY } from "@/modules/tenants/components/org-chooser/org-selected-key";
import { z } from "zod";
import { clearSentryUser } from "../sentry";

export function signOut() {
  clearSentryUser();
  // drop the picked company so the next login re-prompts the chooser
  sessionStorage.removeItem(ORG_SELECTED_KEY);
  // embedded logout route handled by the frontegg login box
  window.location.href = "/account/logout";
}

const fronteggErrorBodySchema = z.object({
  errors: z.array(z.string()).optional(),
  errorCode: z.string().optional(),
  errorCodes: z.array(z.string()).optional(),
});

export type FronteggErrorBody = z.infer<typeof fronteggErrorBodySchema>;

/**
 * Thrown for non-OK Frontegg Identity responses. Carries the HTTP status and
 * any `errors` / `errorCode` fields so UI layers can show the vendor message.
 */
export class FronteggError extends Error {
  readonly status: number;
  readonly errors: string[];
  readonly errorCode?: string;

  constructor(args: {
    errors?: string[];
    status: number;
    message: string;
    errorCode?: string;
  }) {
    super(args.message);
    this.name = "FronteggError";
    this.status = args.status;
    this.errors = args.errors ?? [];
    this.errorCode = args.errorCode;
  }
}

/**
 * Parses a Frontegg Identity error JSON body into the first user-facing message.
 */
export function parseFronteggErrorMessage(json: unknown): string | undefined {
  const parsed = fronteggErrorBodySchema.safeParse(json);
  if (!parsed.success) {
    return undefined;
  }
  const messages = parsed.data.errors?.filter((message) => message.trim());
  if (!messages?.length) {
    return undefined;
  }
  return messages.join(", ");
}

/**
 * Reads `errorCode` or the first entry of `errorCodes` from a Frontegg body.
 */
export function parseFronteggErrorCode(json: unknown): string | undefined {
  const parsed = fronteggErrorBodySchema.safeParse(json);
  if (!parsed.success) {
    return undefined;
  }
  return parsed.data.errorCode ?? parsed.data.errorCodes?.[0];
}

function parseFronteggErrorFields(json: unknown): {
  errors: string[];
  message?: string;
  errorCode?: string;
} {
  const parsed = fronteggErrorBodySchema.safeParse(json);
  if (!parsed.success) {
    return { errors: [] };
  }
  const errors = parsed.data.errors?.filter((message) => message.trim()) ?? [];
  return {
    errors,
    message: errors.length > 0 ? errors.join(", ") : undefined,
    errorCode: parsed.data.errorCode ?? parsed.data.errorCodes?.[0],
  };
}

/**
 * Throws {@link FronteggError} when `response` is not OK, preferring the
 * vendor `errors` message over the status fallback label.
 */
export async function throwIfFronteggFailed(
  response: Response,
  fallbackLabel: string,
): Promise<void> {
  if (response.ok) {
    return;
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new FronteggError({
      status: response.status,
      message: `${fallbackLabel} (${response.status})`,
    });
  }

  const fields = parseFronteggErrorFields(body);
  throw new FronteggError({
    errors: fields.errors,
    status: response.status,
    message: fields.message ?? `${fallbackLabel} (${response.status})`,
    errorCode: fields.errorCode,
  });
}
